import React, { useState, useRef, useEffect, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import api from '../api/api';
import styles from './FichePostePage.module.css';
import {
  Zap,
  FileText,
  ClipboardList,
  Upload,
  CheckCircle2,
  Download,
  X,
  Loader2,
  MapPin,
  Home,
  GraduationCap,
  Sparkles,
  FileOutput,
  AlertCircle,
  Coins,
  Mail
} from 'lucide-react';

const POLLING_TIMEOUT_MS = 10 * 60 * 1000;

const FichePostePage = () => {
  const { quota, refreshToken } = useAuth();

  const [activeTab, setActiveTab] = useState('file');
  const [file, setFile] = useState(null);
  const [constraints, setConstraints] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('idle');
  const [taskId, setTaskId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const pollingInterval = useRef(null);
  const startTimeRef = useRef(null);

  const updateStartTime = useCallback((time) => {
    startTimeRef.current = time;
  }, []);

  const stopProcessing = useCallback(
    (stopStatus, message = '') => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      localStorage.removeItem('fichePosteTask');
      setStatus(stopStatus);
      if (message) setError(message);
      updateStartTime(null);
    },
    [updateStartTime]
  );

  const checkStatus = useCallback(
    async (currentTaskId, isRecovery = false) => {
      if (!currentTaskId) return;
      try {
        const response = await api.get(`/status/${currentTaskId}`);
        const data = response.data;
        if (data.status === 'completed') {
          setJobData(data.result || data);
          stopProcessing('completed');
        } else if (data.status === 'error') {
          stopProcessing('error', data.message || "Erreur lors du traitement par l'IA.");
        } else {
          const startTime = startTimeRef.current;
          if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
            stopProcessing('error', 'Le traitement a dépassé le temps limite (10 min).');
          }
        }
      } catch (err) {
        console.error('Erreur polling:', err);
      }
    },
    [stopProcessing]
  );

  const startPolling = useCallback(
    (currentTaskId) => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      pollingInterval.current = setInterval(() => checkStatus(currentTaskId, false), 10000);
    },
    [checkStatus]
  );

  useEffect(() => {
    const savedTaskJSON = localStorage.getItem('fichePosteTask');
    if (savedTaskJSON) {
      try {
        const savedTask = JSON.parse(savedTaskJSON);
        if (savedTask?.id && savedTask.status === 'processing') {
          setTaskId(savedTask.id);
          setStatus('processing');
          if (savedTask.startTime) updateStartTime(savedTask.startTime);
          if (savedTask.fileName) setFile({ name: savedTask.fileName });
          if (savedTask.mode) setActiveTab(savedTask.mode);
          checkStatus(savedTask.id, true).then(() => {
            const isTimeout =
              startTimeRef.current &&
              Date.now() - startTimeRef.current > POLLING_TIMEOUT_MS;
            if (!isTimeout) startPolling(savedTask.id);
          });
        } else {
          localStorage.removeItem('fichePosteTask');
        }
      } catch {
        localStorage.removeItem('fichePosteTask');
      }
    }
    return () => { if (pollingInterval.current) clearInterval(pollingInterval.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'processing' && taskId) {
        checkStatus(taskId, false);
        startPolling(taskId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [status, taskId, checkStatus, startPolling]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleGenerate = async () => {
    if (activeTab === 'file' && !file) return;
    if (activeTab === 'notes' && !notes.trim()) return;

    setStatus('uploading');
    setError(null);
    setJobData(null);

    try {
      const formData = new FormData();
      formData.append('mode', activeTab);
      if (activeTab === 'file') {
        formData.append('file', file);
        formData.append('constraints', constraints);
      } else {
        formData.append('notes', notes);
      }

      const response = await api.post('/v1/job-builder/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.new_token) refreshToken(response.data.new_token);

      const newTaskId = response.data.task_id;
      const now = Date.now();

      setTaskId(newTaskId);
      setStatus('processing');
      updateStartTime(now);

      localStorage.setItem(
        'fichePosteTask',
        JSON.stringify({
          id: newTaskId,
          status: 'processing',
          fileName: activeTab === 'file' ? file.name : 'Notes brutes',
          mode: activeTab,
          startTime: now,
        })
      );

      startPolling(newTaskId);
    } catch (err) {
      setStatus('error');
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          "Erreur lors de l'envoi des données."
      );
    }
  };

  const resetForm = () => {
    setFile(null);
    setJobData(null);
    setConstraints('');
    setNotes('');
    stopProcessing('idle');
  };

  const handleDownload = async () => {
    if (!jobData?.docx_filename) return;
    try {
      const response = await api.get(
        `/v1/job-builder/download/${jobData.docx_filename}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', jobData.docx_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Impossible de télécharger le fichier.');
    }
  };

  const getSafeList = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return [];
  };

  const isLoading = status === 'uploading' || status === 'processing';
  const isButtonDisabled =
    isLoading ||
    (activeTab === 'file' && !file) ||
    (activeTab === 'notes' && !notes.trim());

  /* ── Labels du bouton ── */
  const btnLabel = {
    uploading: 'Envoi en cours…',
    processing: "L'IA rédige…",
    idle: 'Générer la fiche',
    completed: 'Régénérer',
    error: 'Réessayer',
  }[status];

  /* ── Meta items ── */
  const metaItems = [
    { Icon: MapPin, value: jobData?.location },
    { Icon: Home,   value: jobData?.remote_policy },
    { Icon: GraduationCap, value: jobData?.seniority },
  ];

  return (
    <div className={styles.container}>

      {/* ── HEADER ── */}
      <header className={styles.header}>
        <div className={styles.headerEyebrow}>
          <Zap size={12} strokeWidth={2.5} />
          Propulsé par l'IA
        </div>
        <h1 className={styles.headerTitle}>Job Builder</h1>
        <p className={styles.headerSubtitle}>
          Transformez vos documents et notes en une fiche de poste
          professionnelle en quelques secondes.
        </p>
      </header>

      <main className={styles.mainLayout}>

        {/* ════════════════════════
            COLONNE GAUCHE
        ════════════════════════ */}
        <div className={styles.inputColumn}>

          {/* Quota */}
          {quota !== null && quota !== undefined && (
            <div className={styles.quotaDisplay}>
              <Coins size={14} strokeWidth={2} />
              Crédits restants :&nbsp;<strong>{quota}</strong>
            </div>
          )}

          {/* Tabs */}
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tabBtn} ${activeTab === 'file' ? styles.activeTab : ''}`}
              onClick={() => !isLoading && setActiveTab('file')}
              disabled={isLoading}
            >
              <FileText size={14} strokeWidth={2} />
              Depuis un fichier
            </button>
            <button
              className={`${styles.tabBtn} ${activeTab === 'notes' ? styles.activeTab : ''}`}
              onClick={() => !isLoading && setActiveTab('notes')}
              disabled={isLoading}
            >
              <ClipboardList size={14} strokeWidth={2} />
              Depuis des notes
            </button>
          </div>

          {/* ── Onglet Fichier ── */}
          {activeTab === 'file' ? (
            <>
              {/* Zone Upload */}
              <section
                className={`${styles.uploadCard} ${file ? styles.fileSelected : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => !isLoading && fileInputRef.current.click()}
                style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
              >
                <input
                  type="file"
                  accept=".txt,.pdf,.docx"
                  className={styles.hiddenInput}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <div className={styles.uploadContent}>
                  <div className={styles.uploadIconWrap}>
                    {file
                      ? <CheckCircle2 size={24} strokeWidth={1.75} />
                      : <Upload size={24} strokeWidth={1.75} />
                    }
                  </div>
                  {file ? (
                    <>
                      <p className={styles.fileName}>{file.name}</p>
                      <p className={styles.uploadSub}>Cliquez pour changer de fichier</p>
                    </>
                  ) : (
                    <>
                      <p className={styles.uploadLabel}>Glissez votre fichier ici</p>
                      <p className={styles.uploadSub}>PDF · DOCX · TXT</p>
                    </>
                  )}
                </div>
              </section>

              {/* Contraintes */}
              <div className={styles.formCard}>
                <label className={styles.formLabel} htmlFor="constraints">
                  <Sparkles size={14} strokeWidth={2} />
                  Instructions spécifiques
                </label>
                <textarea
                  id="constraints"
                  className={styles.textArea}
                  placeholder="Ex : Ton formel, insister sur le leadership, poste en télétravail…"
                  value={constraints}
                  onChange={(e) => setConstraints(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </>
          ) : (
            /* ── Onglet Notes ── */
            <div className={styles.formCard}>
              <label className={styles.formLabel} htmlFor="notes">
                <ClipboardList size={14} strokeWidth={2} />
                Vos notes brutes
              </label>
              <textarea
                id="notes"
                className={`${styles.textArea} ${styles.textAreaNotes}`}
                placeholder="Ex : On cherche un Dev React senior, télétravail hybride à Paris, Azure requis, management de 2 personnes…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isLoading}
              />
              <p className={styles.formHint}>
                L'IA va extrapoler ces informations pour générer une fiche
                complète et standardisée.
              </p>
            </div>
          )}

          {/* Bouton Générer */}
          <button
            className={styles.generateBtn}
            onClick={handleGenerate}
            disabled={isButtonDisabled}
          >
            {isLoading && <span className={styles.btnSpinner} />}
            {!isLoading && <FileOutput size={16} strokeWidth={2} />}
            <span>{btnLabel}</span>
          </button>

          {/* Erreur */}
          {error && (
            <div className={styles.errorMsg} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{error}</span>
              </div>
              <a href="/support" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', color: 'inherit', textDecoration: 'underline', marginTop: '5px', alignSelf: 'flex-start' }}>
                <Mail size={13} strokeWidth={2} /> Signaler le problème à un admin
              </a>
            </div>
          )}
        </div>

        {/* ════════════════════════
            COLONNE DROITE
        ════════════════════════ */}
        <div className={styles.previewColumn}>

          {jobData ? (
            /* ── RÉSULTAT ── */
            <article className={styles.paper}>
              <div className={styles.paperInner}>

                {/* Header du paper */}
                <div className={styles.paperHeader}>
                  <div className={styles.paperHeaderLeft}>
                    <span className={styles.badge}>
                      <CheckCircle2 size={11} strokeWidth={2.5} />
                      Générée
                    </span>
                  </div>
                  <div className={styles.paperHeaderRight}>
                    {jobData.docx_filename && (
                      <button
                        onClick={handleDownload}
                        className={styles.downloadBtn}
                        title="Télécharger en Word"
                      >
                        <Download size={14} strokeWidth={2} />
                        Télécharger Word
                      </button>
                    )}
                    <button
                      onClick={resetForm}
                      className={styles.closeBtn}
                      title="Fermer"
                      aria-label="Fermer le résultat"
                    >
                      <X size={16} strokeWidth={2} />
                    </button>
                  </div>
                </div>

                {/* Titre */}
                <h2 className={styles.jobTitle}>
                  {jobData.job_title || 'Titre non détecté'}
                </h2>

                {/* Méta */}
                <div className={styles.metaGrid}>
                  {metaItems.map(({ Icon, value }, i) => (
                    value && (
                      <div key={i} className={styles.metaItem}>
                        <Icon size={14} strokeWidth={2} />
                        {value}
                      </div>
                    )
                  ))}
                </div>

                {/* Description */}
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Description du poste</h3>
                    <div className={styles.sectionDivider} />
                  </div>
                  <p className={styles.jdText}>{jobData.jd_text}</p>
                </section>

                {/* Compétences requises */}
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>Compétences requises</h3>
                    <div className={styles.sectionDivider} />
                  </div>
                  <div className={styles.tagsContainer}>
                    {getSafeList(jobData.required_skills).map((skill, i) => (
                      <span key={i} className={styles.tagPrimary}>{skill}</span>
                    ))}
                  </div>
                </section>

                {/* Bonus */}
                {getSafeList(jobData.bonus_skills).length > 0 && (
                  <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <h3 className={styles.sectionTitle}>Atouts appréciés</h3>
                      <div className={styles.sectionDivider} />
                    </div>
                    <div className={styles.tagsContainer}>
                      {getSafeList(jobData.bonus_skills).map((skill, i) => (
                        <span key={i} className={styles.tagSecondary}>{skill}</span>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </article>
          ) : (
            /* ── ÉTAT VIDE / LOADING ── */
            <div className={styles.emptyPaper}>
              {isLoading ? (
                <div className={styles.loadingState}>
                  <div className={styles.spinnerWrap}>
                    <div className={styles.spinner} />
                    <div className={styles.spinnerInner} />
                  </div>
                  <p className={styles.loadingTitle}>
                    {status === 'uploading'
                      ? 'Transmission en cours…'
                      : "L'IA analyse et rédige votre fiche…"}
                  </p>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} />
                  </div>
                  <p className={styles.loadingHint}>
                    Vous pouvez quitter cette page,
                    <br />le traitement continuera en arrière-plan.
                  </p>
                </div>
              ) : (
                <>
                  <div className={styles.emptyIcon}>
                    <FileText size={32} strokeWidth={1.25} />
                  </div>
                  <p className={styles.emptyTitle}>Votre fiche apparaîtra ici</p>
                  <p className={styles.emptySubtitle}>
                    Importez un document ou saisissez vos notes,
                    puis cliquez sur "Générer la fiche".
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FichePostePage;